import { useMemo, useState } from 'react';

import {
  faSidebarFlip,
  faSparkles,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faCheckCircle,
  faCircleInfo,
  faEllipsis,
  faExclamationCircle,
  faMicrophone,
  faMicrophoneSlash,
  faSpinner,
  faTimes,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SheetTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';

import { isRecordingSessionActiveAtom } from '@/modules/videaNotes/atoms';
import {
  VideaNotesUIState,
  useOpenVideaNotes,
} from '@/modules/videaNotes/helpers';
import { useIsSameAppointment } from '@/modules/videaNotes/hooks/useIsSameAppointment';
import { VoiceNotesSource } from '@/modules/videaNotes/types';
import { useExistingNotes } from '@/react-query/videa-notes/useExistingNotes';
import { useWindowSize } from '@/utils/hooks/useWindowSize';

import { selectedVisitAtom } from '../../../../app/appHeader/imageViewerMenu/atoms';
import { useGetDateFNSLocale } from '../../../../locales/helpers';
import type { Appointment } from '../../../../react-query/useAppointments';
import type { GetTaskResponse } from '../../../../react-query/useGetTasks';
import { usePreferences } from '../../../../react-query/usePreferences';
import type {
  TProcedureStatus,
  TVisitSummary,
} from '../../../../react-query/useVisitSummaries';
import { useDevtoolsShortcut } from '../../../../utils/hooks/useDevtoolsShortcut';
import { useIsInsurancePDFEnabled } from '../../../../utils/hooks/useIsInsurancePDFEnabled';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import {
  claimSummaryDialogAtom,
  useLocalizedFormatDate,
} from '../../../claims/summary/state';
import { isActionableViewAtom } from '../../atoms';
import { getProviderName } from '../../huddlePrintoutContent/helpers';
import { PatientListPageLayoutContextMap } from '../../types';
import InfoDialog from '../InfoDialog';
import { PatientStripOdontogram } from '../patientStripOdontogram/patientStripOdontogram';
import { RecommendedActionsRedesigned } from '../recommendedActions/huddleRedesign/recommendedActionsRedesigned';

import { AppointmentInfo } from './AppointmentInfo';
import { DateDisplay } from './DateDisplay';
import { PatientInfo, getPatientAge } from './patientInfoSheet/PatientInfo';
import { PerioPatientPill } from './perioPatientPill';
import { RBLCloverPill } from './RBLCloverPill';
import { ReferralStatusIcon } from './ReferralStatusIcon';
import { ReferralStatusPill } from './ReferralStatusPill';
import { ReviewFindingsButton } from './ReviewFindingsButton';
import { ScheduledTodayPill } from './ScheduledTodayPill';
import {
  TreatmentCounts,
  actionableStatuses,
  allStatuses,
} from './TreatmentCounts';

const BREAKPOINT_WIDTH = 1280;
const SMALL_SCREEN_SCALE_FACTOR = 1.15;
const LARGE_SCREEN_SCALE_FACTOR = 1.3;

interface PatientStripRedesignedProps {
  appointment: Appointment;
  visitSummary?: TVisitSummary;
  patientTask?: GetTaskResponse;
  index: number;
  onReviewRadiographs: () => void;
  annotatorUri?: string;
  lastViewedAppointmentRef?: React.RefObject<HTMLDivElement>;
  onOpenSheet?: (
    appointment: Appointment,
    source: 'PATIENT_INFO' | 'ICON',
  ) => void;
  onOpenReferModal: () => void;
  onFlagReferral?: () => void;
  onUnmarkReferral?: () => void;
  onResubmitReferral?: () => void;
  isResubmitting?: boolean;
  isResubmitSuccess?: boolean;
  isResubmitError?: boolean;
  resubmitData?: any;
  resetResubmit?: () => void;
  isLoadingVisitSummaries?: boolean;
}

export const PatientStripRedesigned = ({
  appointment,
  index,
  lastViewedAppointmentRef,
  patientTask,
  visitSummary,
  onReviewRadiographs,
  onOpenSheet,
  annotatorUri,
  onOpenReferModal,
  onFlagReferral,
  onUnmarkReferral,
  onResubmitReferral,
  isResubmitting,
  isResubmitSuccess,
  isResubmitError,
  resubmitData,
  resetResubmit,
  isLoadingVisitSummaries,
}: PatientStripRedesignedProps) => {
  // atoms
  const isActionableView = useAtomValue(isActionableViewAtom);
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const setDialogValues = useSetAtom(claimSummaryDialogAtom);
  const openVideaNotes = useOpenVideaNotes();
  const setSelectedVisit = useSetAtom(selectedVisitAtom);

  const user = useSelector((state: any) => state?.login?.user);

  // local state
  const [isOpenDevTools, setIsOpenDevTools] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<TProcedureStatus[]>(
    isActionableView ? actionableStatuses : allStatuses,
  );

  // hooks
  const isInsurancePDFEnabled = useIsInsurancePDFEnabled();
  const { trackEvent } = useTrackEvent();
  const { showDevtools } = useDevtoolsShortcut();
  const locale = useGetDateFNSLocale();
  const formatDate = useLocalizedFormatDate();
  const { data: prefs } = usePreferences();
  const { windowWidth } = useWindowSize();

  // variables
  const imagesDate = appointment.appointment.imagesAt;
  const showInsModalButton =
    isInsurancePDFEnabled && imagesDate && prefs?.insSummaryModal;
  const isVideaNotesEnabled = prefs?.isVideaNotesEnabled;

  const selectedTreatmentCodes = useMemo(() => {
    return visitSummary?.teeth
      .map((tooth) => tooth.treatments?.map((treatment) => treatment.txCode))
      .flat()
      .filter(Boolean);
  }, [visitSummary]);

  // handlers
  const handleClickOpen = () => {
    // Check if required IDs are missing
    if (
      !appointment.appointment.unifiedId ||
      !appointment.appointment.estimatedVisitId
    ) {
      trackEvent(mixpanelEvents.insuranceSummary, {
        source: 'summary-only button',
        error: 'missing_required_ids',
        hasUnifiedId: !!appointment.appointment.unifiedId,
        hasEstimatedVisitId: !!appointment.appointment.estimatedVisitId,
      });
      return;
    }

    const estimatedVisitId =
      appointment.appointment.estimatedVisitId?.toString() ?? null;
    setSelectedVisit(estimatedVisitId);

    setDialogValues({
      isOpen: true,
      unifiedId: appointment.appointment.unifiedId,
      estimatedVisitId: estimatedVisitId ?? '',
      view: 'image',
      appointment,
      patientInfo: {
        dob: formatDate(appointment.patient.dob ?? '', true),
        name: `${appointment.patient.firstName} ${appointment.patient.lastName}`,
        dateOfVisit: appointment.appointment.start,
        radiographsTakenDate: appointment.appointment.imagesAt || '',
        providerName: getProviderName(appointment?.appointment.provider),
      },
      imagingPatientId: appointment.patient.imagingPatientId ?? '',
      locale,
    });

    selectedTreatmentCodes?.length &&
      trackEvent(mixpanelEvents.viewFreeAttachment, {
        unifiedId: appointment.appointment.unifiedId,
        estimatedVisitId: appointment.appointment.estimatedVisitId,
        entryPoint: 'Huddle',
        txCode: selectedTreatmentCodes,
        attachmentType:
          prefs?.insuranceExportPreviewPages === 'single' ? 'Digital' : 'Print',
      });

    trackEvent(mixpanelEvents.insuranceSummary, {
      source: 'summary-only button',
    });
  };

  const isSameAppointmentAsCurrentRecording = useIsSameAppointment({
    estimatedVisitId: appointment.appointment.estimatedVisitId,
    unifiedId: appointment.appointment.unifiedId,
  });

  // Check for existing notes to determine the correct UI state
  const { data: existingNotes = [] } = useExistingNotes(
    appointment.appointment.unifiedId,
    !!isVideaNotesEnabled && !!appointment.appointment.unifiedId,
  );

  const handleOpenVideaNotes = () => {
    // If there are existing notes, show SUMMARY_RESULT view
    // Otherwise, show AUDIO_CAPTURE view to start a new note
    const uiState =
      existingNotes.length > 0
        ? VideaNotesUIState.SUMMARY_RESULT
        : VideaNotesUIState.AUDIO_CAPTURE;
    openVideaNotes(
      user,
      appointment,
      uiState,
      VoiceNotesSource.VOICE_NOTES_BUTTON,
    );
  };

  return (
    <div className="vai-flex vai-flex-col">
      {/* Resubmission Status Alert */}
      {(isResubmitting || isResubmitSuccess || isResubmitError) && (
        <Alert
          variant={isResubmitError ? 'destructive' : 'default'}
          className="vai-mb-2 vai-mx-1 vai-relative"
        >
          {isResubmitting && (
            <FontAwesomeIcon
              icon={faSpinner}
              className="vai-h-4 vai-w-4 vai-animate-spin"
            />
          )}
          {isResubmitSuccess && (
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="vai-h-4 vai-w-4 vai-text-green-600"
            />
          )}
          {isResubmitError && (
            <FontAwesomeIcon icon={faTimesCircle} className="vai-h-4 vai-w-4" />
          )}
          <AlertTitle>
            {isResubmitting && <Trans>Resubmitting referral...</Trans>}
            {isResubmitSuccess && (
              <Trans>Referral resubmitted successfully!</Trans>
            )}
            {isResubmitError && <Trans>Resubmission failed</Trans>}
          </AlertTitle>
          <AlertDescription>
            {isResubmitting && (
              <Trans>Please wait while we resubmit your referral.</Trans>
            )}
            {isResubmitSuccess && (
              <Trans>Your referral has been resubmitted successfully.</Trans>
            )}
            {isResubmitError && (
              <Trans>
                Failed to resubmit referral. Please try again or contact
                support.
              </Trans>
            )}
          </AlertDescription>
          {(isResubmitSuccess || isResubmitError) && (
            <button
              onClick={resetResubmit}
              className="vai-absolute vai-top-2 vai-right-2 vai-p-1 vai-rounded-md hover:vai-bg-gray-100 vai-transition-colors"
              aria-label="Dismiss alert"
            >
              <FontAwesomeIcon icon={faTimes} className="vai-h-4 vai-w-4" />
            </button>
          )}
        </Alert>
      )}

      <div
        className="vai-grid vai-grid-cols-[1fr_1fr_1fr_2.25fr_0.75fr] min-[1640px]:vai-grid-cols-[1fr_1fr_1fr_1.5fr_0.75fr] vai-w-xl vai-px-6 vai-py-3 vai-m-1 vai-mb-2 vai-bg-slate-50 vai-border-[#E7E8E9] vai-rounded-lg vai-shadow-sm vai-shadow-zinc-200"
        ref={lastViewedAppointmentRef}
      >
        <div className="vai-flex vai-flex-col">
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center vai-mb-4 vai-flex-wrap">
            <ScheduledTodayPill
              appointment={appointment}
              visitSummary={visitSummary}
            />
            <PerioPatientPill visitSummary={visitSummary} />
            <RBLCloverPill
              dateOfBirth={appointment.patient.dob}
              visitSummary={visitSummary}
            />
            {prefs?.enableVideaReferral && (
              <ReferralStatusPill
                unifiedAppointmentId={appointment.appointment.unifiedId}
              />
            )}
          </div>
          <PatientInfo
            appointment={appointment}
            context={PatientListPageLayoutContextMap.Huddle}
            index={index}
            onClick={() => onOpenSheet?.(appointment, 'PATIENT_INFO')}
          />
          <AppointmentInfo appointment={appointment} isIncludeOperatory />
        </div>
        <div className="vai-flex vai-flex-col">
          <RecommendedActionsRedesigned
            appointment={appointment}
            patientTask={patientTask}
          />
        </div>
        <div>
          <TreatmentCounts
            appointment={appointment}
            visitSummary={visitSummary}
            setSelectedStatuses={setSelectedStatuses}
            isLoading={isLoadingVisitSummaries}
          />
        </div>
        <PatientStripOdontogram
          age={getPatientAge(appointment.patient.dob)}
          visitSummary={visitSummary}
          selectedStatuses={selectedStatuses}
          appointment={appointment}
          scaleFactor={
            windowWidth >= BREAKPOINT_WIDTH
              ? LARGE_SCREEN_SCALE_FACTOR
              : SMALL_SCREEN_SCALE_FACTOR
          }
        />
        <div className="vai-flex vai-flex-col vai-items-end vai-justify-between vai-ml-2">
          <div className="vai-flex vai-flex-row-reverse vai-gap-2">
            {/* sheet trigger is a part of Sheet element from ShadCN
            https://ui.shadcn.com/docs/components/sheet (see Sheet element at
            PatientList) */}
            <SheetTrigger asChild>
              <button
                className="vai-flex"
                onClick={() => onOpenSheet?.(appointment, 'ICON')}
              >
                <FontAwesomeIcon
                  className="vai-text-[#4A4F55]"
                  icon={faSidebarFlip}
                />
              </button>
            </SheetTrigger>

            {/* this button indirectly triggers open the Sheet that houses Videa Notes.
            This is currently behind a feature flag */}
            {isVideaNotesEnabled && (
              <button
                className="vai-flex"
                onClick={handleOpenVideaNotes}
                disabled={!isSameAppointmentAsCurrentRecording}
              >
                <FontAwesomeIcon
                  className={classNames('', {
                    'vai-text-red-600':
                      isRecordingSessionActive &&
                      isSameAppointmentAsCurrentRecording,
                    'vai-text-gray-600': !isRecordingSessionActive,
                    'vai-cursor-not-allowed':
                      isRecordingSessionActive &&
                      !isSameAppointmentAsCurrentRecording,
                  })}
                  icon={
                    !isSameAppointmentAsCurrentRecording
                      ? faMicrophoneSlash
                      : faMicrophone
                  }
                />
              </button>
            )}

            <div className="vai-flex vai-flex-row vai-justify-end vai-w-full vai-text-text-accent vai-font-thin">
              {showInsModalButton && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    asChild
                    data-testid="generate-insurance-menu"
                  >
                    <div className="vai-flex vai-items-center vai-justify-center vai-w-fit vai-h-fit vai-m-0 vai-bg-transparent vai-border-none vai-cursor-pointer hover:vai-bg-grey-200 vai-rounded-md vai-px-2 vai-py-0 vai-transition-colors vai-duration-200">
                      <FontAwesomeIcon
                        icon={faEllipsis}
                        transform="shrink-1 grow-3"
                      />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {showInsModalButton && (
                      <DropdownMenuItem
                        onClick={handleClickOpen}
                        className="vai-text-sm vai-w-fit vai-h-fit vai-cursor-pointer"
                        role="button"
                        data-testid="generate-insurance-button"
                      >
                        <FontAwesomeIcon icon={faSparkles} />
                        <Trans>Generate Insurance JPGs</Trans>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {prefs?.enableVideaReferral && (
              <ReferralStatusIcon
                unifiedAppointmentId={appointment.appointment.unifiedId}
                onOpenReferModal={onOpenReferModal}
                onFlagReferral={onFlagReferral}
                onUnmarkReferral={onUnmarkReferral}
                onResubmitReferral={onResubmitReferral}
              />
            )}
          </div>
          <div className="vai-flex vai-flex-row vai-items-center vai-justify-end vai-gap-1 vai-w-fit">
            {appointment.patient.imagingPatientId ? (
              <ReviewFindingsButton
                appointment={appointment}
                onReviewRadiographs={onReviewRadiographs}
                annotatorUri={annotatorUri}
              />
            ) : (
              <span className="vai-text-sm vai-text-text-secondary">
                <FontAwesomeIcon icon={faCircleInfo} />{' '}
                <Trans>No images available</Trans>
              </span>
            )}
          </div>
          <div className="vai-flex vai-flex-row vai-w-full vai-items-center vai-justify-end vai-gap-1">
            {showDevtools && (
              <FontAwesomeIcon
                className="vai-w-4 vai-h-4 vai-text-blue-600"
                icon={faExclamationCircle}
                onClick={() => {
                  setIsOpenDevTools(true);
                }}
              />
            )}
            {appointment.appointment.imagesAt && (
              <DateDisplay date={appointment.appointment.imagesAt} />
            )}
          </div>
          <InfoDialog
            appointment={appointment}
            onClose={() => setIsOpenDevTools(false)}
            isOpen={isOpenDevTools}
            patientTask={patientTask}
            fmxSummary={visitSummary}
          />
        </div>
      </div>
    </div>
  );
};
